'use client';

import { useRef, useEffect, useCallback } from 'react';

import Image from 'next/image';
import { useRouter } from 'next/navigation';

import { Button, Checkbox } from '@paction-2.0/ui';
import { MARKETING_EMAIL_CONSENT_LABEL } from '@paction-2.0/util/constants/marketingConsent';
import {
  INTRO_MESSAGE_MAX_LENGTH,
  INTRO_MESSAGE_MIN_LENGTH
} from '@paction-2.0/util/validation';

import {
  HouseHeartIcon,
  TwoWayArrowIcon,
  PuppyIcon
} from '@/components/IntroIcons';

import { MissingRequirementsHint } from '@/components/MissingRequirementsHint';
import {
  INTRO_PLACEHOLDER,
  INTRO_REPLY_RATE_LINE,
  INTRO_SECTION_LABEL
} from '@/features/dialogs/IntroMessagePrompt/introCopy';
import { InlineAccountCompletion } from '@/features/auth/components/InlineAccountCompletion';
import { SmsConsentNotice } from '@/features/auth/components/SmsConsentNotice/SmsConsentNotice';
import { HandbookWaitlistPrompt } from '@/features/purchaser/handbooks/HandbookWaitlistPrompt';
import { VerificationPanel } from '@/features/verification';
import {
  useGlobalWaitlistWidget,
  type UseGlobalWaitlistWidgetProps
} from './useGlobalWaitlistWidget';
import { useWaitlistDock } from './useWaitlistDock';
import styles from './styles/globalWaitlistWidget.module.scss';

export type GlobalWaitlistWidgetProps = UseGlobalWaitlistWidgetProps & {
  /**
   * `docked` keeps the waitlist reachable from anywhere on the page: a collapsed
   * bar sits above the bottom nav until you scroll down to the inline card, and
   * tapping it opens the form full-screen. `inline` is the plain card.
   */
  presentation?: 'inline' | 'docked';
};

type WaitlistCardContentProps = {
  widget: ReturnType<typeof useGlobalWaitlistWidget>;
};

const ChevronIcon = ({ direction }: { direction: 'up' | 'down' }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={direction === 'up' ? 'M18 15l-6-6-6 6' : 'M6 9l6 6 6-6'} />
  </svg>
);

/**
 * The form itself. Rendered exactly once regardless of presentation — the
 * inline card and the full-screen overlay are the same element restyled, so
 * anything typed survives collapsing and expanding.
 */
function WaitlistCardContent({ widget }: WaitlistCardContentProps) {
  const {
    form,
    handleChange,
    handleBlur,
    handleFieldBlur,
    fieldErrors,
    provinceOptions,
    cityOptions,
    isSubmitting,
    submitError,
    submitSuccess,
    alreadyOnWaitlist,
    marketingEmailOptIn,
    toggleMarketingEmailOptIn,
    title,
    description,
    isFormValid,
    missingRequirements,
    hasStartedForm,
    verification,
    justSignedIn,
    isFieldDisabled,
    handleSubmit,
    isAuthenticated,
    submittedEmail,
    submittedPhone,
    automationResult,
    resumedFromEmail,
    breedName,
    breedSlug
  } = widget;

  const router = useRouter();

  const showIntroMessage = !!(
    form.firstName.trim() &&
    form.email.trim() &&
    form.provinceSlug &&
    form.citySlug
  );

  const introSectionRef = useRef<HTMLDivElement>(null);
  const disclaimerRef = useRef<HTMLParagraphElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const hasConversations =
    automationResult &&
    automationResult.providersConnected > 0 &&
    automationResult.conversationsCreated > 0;

  const introLength = form.introMessage.trim().length;
  const introNeedsMoreCharacters =
    introLength > 0 && introLength < INTRO_MESSAGE_MIN_LENGTH;

  const isSignedIn = isAuthenticated || justSignedIn;

  const accountRedirectPath = hasConversations
    ? '/messages'
    : '/purchaser/dashboard';

  useEffect(() => {
    if (submitSuccess && !isSignedIn) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [submitSuccess, isSignedIn]);

  useEffect(() => {
    if (submitSuccess && isSignedIn) {
      router.push(accountRedirectPath);
    }
  }, [submitSuccess, isSignedIn, accountRedirectPath, router]);

  useEffect(() => {
    if (showIntroMessage) {
      // Scroll the disclaimer (below the submit button) into view so the
      // button is guaranteed to be visible above it.
      disclaimerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    }
  }, [showIntroMessage]);

  const handleAccountComplete = useCallback(
    (redirect: string) => {
      router.push(redirect);
    },
    [router]
  );

  const completionSource = 'global_waitlist_widget';

  const completeSignupHref = submittedEmail
    ? `/complete-signup?email=${encodeURIComponent(submittedEmail)}&next=/messages&source=${encodeURIComponent(completionSource)}`
    : `/complete-signup?source=${encodeURIComponent(completionSource)}`;

  if (submitSuccess && !submitError && isSignedIn) {
    return (
      <div className={styles.card} ref={cardRef}>
        <div className={styles.successView}>
          <h2 className={styles.successHeading}>
            {hasConversations
              ? 'Taking you to your messages…'
              : 'Taking you to your profile…'}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card} ref={cardRef}>
      {submitSuccess && !submitError ? (
        <div className={styles.successView}>
          <Image
            src="/icons/success-confetti.svg"
            alt=""
            width={96}
            height={96}
            className={styles.successIcon}
          />
          {hasConversations ? (
            <>
              <h2 className={styles.successHeading}>Great News!</h2>
              <p className={styles.successSubtext}>
                Paction has connected you to{' '}
                <strong>
                  {automationResult.providersConnected}{' '}
                  {automationResult.providersConnected === 1
                    ? 'breeder'
                    : 'breeders'}
                </strong>{' '}
                {automationResult.providersConnected === 1
                  ? 'who is'
                  : 'who are'}{' '}
                ready to speak! Complete your sign-up to log in and continue the
                conversation.
              </p>
            </>
          ) : (
            <>
              <h2 className={styles.successHeading}>You&apos;re on the list</h2>
              <p className={styles.successSubtext}>
                Paction is reaching out to Canadian breeders who align with what
                you&apos;re looking for. Complete your sign-up to log in and
                manage how you&apos;d like to be notified.
              </p>
            </>
          )}
          {submittedEmail ? (
            <InlineAccountCompletion
              email={submittedEmail}
              initialPhone={submittedPhone ?? undefined}
              redirectPath={accountRedirectPath}
              onComplete={handleAccountComplete}
              completionSource={completionSource}
            />
          ) : (
            <Button variant="primary" href={completeSignupHref}>
              Complete your account
            </Button>
          )}
        </div>
      ) : (
        <>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.description}>{description}</p>
          {resumedFromEmail && (
            <p className={styles.fieldHint} role="status">
              Welcome back. We saved your details and intro. Check them, then
              tap Join Waitlist to send your request.
            </p>
          )}

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.rowSplit}>
              <div className={styles.field}>
                <label htmlFor="gwl-firstName">
                  First Name
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                <input
                  id="gwl-firstName"
                  type="text"
                  value={form.firstName}
                  onChange={handleChange('firstName')}
                  onBlur={handleBlur('firstName')}
                  required
                  minLength={3}
                  readOnly={isFieldDisabled}
                  autoComplete="given-name"
                  className={
                    fieldErrors.firstName
                      ? styles.inputError
                      : isFieldDisabled
                        ? styles.inputReadOnly
                        : undefined
                  }
                  aria-invalid={!!fieldErrors.firstName}
                  aria-describedby={
                    fieldErrors.firstName ? 'gwl-firstName-error' : undefined
                  }
                />
                {fieldErrors.firstName && (
                  <span
                    id="gwl-firstName-error"
                    className={styles.fieldError}
                    role="alert"
                  >
                    {fieldErrors.firstName}
                  </span>
                )}
              </div>
              <div className={styles.field}>
                <label htmlFor="gwl-lastName">
                  Last Name
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                <input
                  id="gwl-lastName"
                  type="text"
                  value={form.lastName}
                  onChange={handleChange('lastName')}
                  onBlur={handleFieldBlur('lastName')}
                  required
                  readOnly={isFieldDisabled}
                  autoComplete="family-name"
                  className={isFieldDisabled ? styles.inputReadOnly : undefined}
                />
              </div>
            </div>

            <div className={styles.rowSplit}>
              <div className={styles.field}>
                <label htmlFor="gwl-email">
                  Email Address
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                <input
                  id="gwl-email"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  onBlur={handleBlur('email')}
                  required
                  readOnly={isFieldDisabled}
                  autoComplete="email"
                  className={
                    `${isFieldDisabled ? styles.inputReadOnly : ''} ${fieldErrors.email ? styles.inputError : ''}`.trim() ||
                    undefined
                  }
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={
                    fieldErrors.email ? 'gwl-email-error' : undefined
                  }
                />
                {fieldErrors.email && (
                  <span
                    id="gwl-email-error"
                    className={styles.fieldError}
                    role="alert"
                  >
                    {fieldErrors.email}
                  </span>
                )}
              </div>
              <div className={styles.field}>
                <label htmlFor="gwl-phone">Mobile Number</label>
                <input
                  id="gwl-phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange('phone')}
                  onBlur={handleBlur('phone')}
                  readOnly={isFieldDisabled}
                  autoComplete="tel"
                  className={
                    `${isFieldDisabled ? styles.inputReadOnly : ''} ${fieldErrors.phone ? styles.inputError : ''}`.trim() ||
                    undefined
                  }
                  aria-invalid={!!fieldErrors.phone}
                  aria-describedby={
                    fieldErrors.phone ? 'gwl-phone-error' : 'gwl-phone-hint'
                  }
                />
                {fieldErrors.phone ? (
                  <span
                    id="gwl-phone-error"
                    className={styles.fieldError}
                    role="alert"
                  >
                    {fieldErrors.phone}
                  </span>
                ) : (
                  <span id="gwl-phone-hint" className={styles.fieldHint}>
                    Get a text when a breeder replies, so you don&apos;t miss
                    them.
                  </span>
                )}
              </div>
            </div>

            {!alreadyOnWaitlist && (
              <SmsConsentNotice className={styles.smsConsent} />
            )}

            <div className={styles.rowSplit}>
              <div className={styles.field}>
                <label htmlFor="gwl-province">
                  Province
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                <select
                  id="gwl-province"
                  value={form.provinceSlug}
                  onChange={handleChange('provinceSlug')}
                  required
                  disabled={alreadyOnWaitlist}
                  className={
                    alreadyOnWaitlist ? styles.inputReadOnly : undefined
                  }
                >
                  <option value="">Select a province</option>
                  {provinceOptions.map((province) => (
                    <option key={province.slug} value={province.slug}>
                      {province.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label htmlFor="gwl-city">
                  City (nearest)
                  <span className={styles.requiredAsterisk}>*</span>
                </label>
                <select
                  id="gwl-city"
                  value={form.citySlug}
                  onChange={handleChange('citySlug')}
                  required
                  disabled={!form.provinceSlug || alreadyOnWaitlist}
                  className={
                    !form.provinceSlug || alreadyOnWaitlist
                      ? styles.inputReadOnly
                      : undefined
                  }
                >
                  <option value="">
                    {form.provinceSlug
                      ? 'Select a city'
                      : 'Select province first'}
                  </option>
                  {cityOptions.map((city) => (
                    <option key={city.slug} value={city.slug}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {showIntroMessage && (
              <>
                <hr className={styles.divider} />
                <div className={styles.field} ref={introSectionRef}>
                  <label
                    htmlFor="gwl-introMessage"
                    className={styles.subtitleLabel}
                  >
                    {INTRO_SECTION_LABEL}
                  </label>
                  <div className={styles.introIcons}>
                    <HouseHeartIcon size={64} />
                    <TwoWayArrowIcon size={64} />
                    <PuppyIcon size={64} />
                  </div>
                  <p className={styles.introCatchphrase}>
                    {INTRO_REPLY_RATE_LINE}
                  </p>
                  <textarea
                    id="gwl-introMessage"
                    value={form.introMessage}
                    onChange={handleChange('introMessage')}
                    onBlur={handleFieldBlur('introMessage')}
                    readOnly={alreadyOnWaitlist}
                    maxLength={INTRO_MESSAGE_MAX_LENGTH}
                    rows={3}
                    placeholder={INTRO_PLACEHOLDER}
                    className={
                      fieldErrors.introMessage
                        ? styles.inputError
                        : alreadyOnWaitlist
                          ? styles.inputReadOnly
                          : undefined
                    }
                    aria-invalid={!!fieldErrors.introMessage}
                    aria-describedby={
                      fieldErrors.introMessage
                        ? 'gwl-introMessage-error'
                        : undefined
                    }
                  />
                  {fieldErrors.introMessage && (
                    <span
                      id="gwl-introMessage-error"
                      className={styles.fieldError}
                      role="alert"
                    >
                      {fieldErrors.introMessage}
                    </span>
                  )}
                  <span className={styles.charCount}>
                    {introNeedsMoreCharacters
                      ? `${form.introMessage.trim().length} / ${INTRO_MESSAGE_MIN_LENGTH} characters minimum`
                      : `${form.introMessage.length}/${INTRO_MESSAGE_MAX_LENGTH}`}
                  </span>
                </div>
              </>
            )}

            {!alreadyOnWaitlist && (
              <div className={styles.marketingOptIn}>
                <Checkbox
                  checked={marketingEmailOptIn}
                  onChange={toggleMarketingEmailOptIn}
                  label={
                    <span className={styles.marketingOptInText}>
                      {MARKETING_EMAIL_CONSENT_LABEL}
                    </span>
                  }
                />
              </div>
            )}

            <div className={styles.actions}>
              {alreadyOnWaitlist ? (
                <div className={styles.alreadyOnWaitlist}>
                  You&apos;re on this waitlist.
                  <HandbookWaitlistPrompt
                    breedName={breedName}
                    breedSlug={breedSlug}
                  />
                </div>
              ) : verification.isOpen ? (
                <VerificationPanel
                  channel={verification.channel}
                  destination={verification.destination}
                  code={verification.code}
                  isSending={verification.isSending}
                  isConfirming={verification.isConfirming}
                  error={verification.error}
                  resent={verification.resent}
                  onCodeChange={verification.handleCodeChange}
                  onVerify={() => void verification.confirm()}
                  onResend={() => void verification.resend()}
                  onCancel={verification.cancel}
                  altChannel={
                    verification.channel === 'phone'
                      ? form.email.trim()
                        ? {
                            channel: 'email' as const,
                            destination: form.email.trim()
                          }
                        : null
                      : form.phone.trim()
                        ? {
                            channel: 'phone' as const,
                            destination: form.phone.trim()
                          }
                        : null
                  }
                  onSwitchChannel={verification.switchChannel}
                />
              ) : (
                <>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!isFormValid || isSubmitting}
                    aria-describedby={
                      !isFormValid && hasStartedForm
                        ? 'gwl-missing-requirements'
                        : undefined
                    }
                  >
                    {isSubmitting ? 'Joining…' : 'Join Waitlist'}
                  </Button>
                  {hasStartedForm && (
                    <MissingRequirementsHint
                      id="gwl-missing-requirements"
                      className={styles.actionsHint}
                      requirements={missingRequirements}
                    />
                  )}
                </>
              )}
            </div>

            <p className={styles.disclaimer} ref={disclaimerRef}>
              We will email you about this waitlist and the breeders it connects
              you with. Unsubscribe anytime.
            </p>
          </form>

          {submitError && (
            <div className={styles.feedback}>
              <div className={styles.feedbackError} role="alert">
                <strong>Error:</strong> {submitError}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function GlobalWaitlistWidget({
  presentation = 'inline',
  ...props
}: GlobalWaitlistWidgetProps) {
  const widget = useGlobalWaitlistWidget(props);
  const dock = useWaitlistDock({
    enabled: presentation === 'docked',
    breedSlug: props.breedSlug,
    provinceSlug: props.provinceSlug ?? undefined,
    citySlug: props.citySlug
  });

  const { isDocked, isExpanded, isBarVisible, hasSettled } = dock;
  const isJoined = widget.submitSuccess || widget.alreadyOnWaitlist;
  const { resumedFromEmail } = widget;
  const { expand } = dock;

  useEffect(() => {
    if (!resumedFromEmail) return;
    if (isDocked) {
      expand();
      return;
    }
    document
      .getElementById('global-waitlist')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [resumedFromEmail, isDocked, expand]);

  const barClassName = [
    styles.dockBar,
    hasSettled ? styles.dockBarReady : null,
    isBarVisible ? null : styles.dockBarHidden
  ]
    .filter(Boolean)
    .join(' ');

  const panelClassName = isExpanded
    ? `${styles.panel} ${styles.panelExpanded}`
    : styles.panel;

  return (
    <>
      <section
        className={styles.widget}
        id="global-waitlist"
        ref={dock.sectionRef}
        // Holds the section's height while the card is lifted out of the flow,
        // so nothing behind the overlay reflows (and the scroll position is
        // exactly where the user left it on close).
        style={
          dock.placeholderHeight
            ? { minHeight: `${dock.placeholderHeight}px` }
            : undefined
        }
      >
        <div
          className={panelClassName}
          ref={dock.panelRef}
          {...(isExpanded
            ? {
                role: 'dialog' as const,
                'aria-modal': true,
                'aria-label': widget.title,
                tabIndex: -1
              }
            : {})}
        >
          {isExpanded && (
            <button
              type="button"
              className={styles.minimizeButton}
              onClick={dock.collapse}
              aria-label="Minimize waitlist form"
            >
              <ChevronIcon direction="down" />
            </button>
          )}
          <WaitlistCardContent widget={widget} />
        </div>
      </section>

      {isDocked && (
        <button
          type="button"
          ref={dock.barRef}
          className={barClassName}
          onClick={dock.expand}
          aria-expanded={isExpanded}
          aria-controls="global-waitlist"
          aria-hidden={!isBarVisible}
          tabIndex={isBarVisible ? 0 : -1}
        >
          <span className={styles.dockBarIcon}>
            <PuppyIcon size={28} />
          </span>
          <span className={styles.dockBarLabel}>
            {isJoined ? "You're on the list" : widget.title}
          </span>
          <span className={styles.dockBarChevron}>
            <ChevronIcon direction="up" />
          </span>
        </button>
      )}
    </>
  );
}
